/**
 * Morse „Sprechen“ — der Anschluss ans vorhandene Live-Diktat. (08.10.2026)
 *
 * KEIN zweiter Aufnahme-Motor: das Mikrofon kommt über denselben Weg wie in jedem Textfeld (feld-mikrofon.ts) — das
 * Live-Diktat (live-dictation.ts), das die EINE Mikro-Leitung der App benutzt (`acquireDictationStream`: warmes Mikro
 * übernehmen, sonst die Tagesaufnahme weich anhalten) und die Mitschrift in Echtzeit liefert. Auf dem Gerät wird nichts
 * gespeichert; die Mitschrift lebt im Speicher.
 *
 * Was dieser Anschluss zusätzlich tut — und warum:
 *   - `sitzungZurueck()` vor dem Start: Eine Gruppe lässt die Tonsitzung noch bis zu 1,5 s auf `playback` (tts-pcm.ts,
 *     `sitzungHalten`/`sitzungLoslassen`), und `playback` kann nicht aufnehmen — die Seite bekäme Stille (setSeitenAufnahme
 *     in playback-hifi.ts erzählt denselben Vorfall vom 25.09.). tts-pcm.ts bleibt der EINE Besitzer der Sitzung.
 *   - die Sperren: Anruf (ptt.ts), Sprechtaste (playback-hifi.ts), laufende Wiedergabe (seiten-ton.ts).
 *   - die Fehler in Arten übersetzt (kein Recht, kein Mikrofon, Netz/Dienst, sonst) — der Knopf hat für jede einen Satz.
 *
 * ⚠️ `starte` läuft SYNCHRON bis zum ersten `await` in `LiveDictation.start`: dort entsteht der AudioContext, und iOS gibt
 *    ihn nur in der Geste des Tipps frei. Kein `await` und kein dynamischer `import` davor.
 * ⚠️ Nur am echten iPhone zu prüfen: Mikrofon neben dem Ton der Übung, das Wach-Wort danach, die Tagesaufnahme.
 */
import { LiveDictation } from "./live-dictation.js";
import { istAnrufAktiv } from "./ptt.js";
import { seitenAufnahmeLaeuft } from "./playback-hifi.js";
import { seitenTonMelder } from "./seiten-ton.js";
import { sitzungZurueck } from "./tts-pcm.js";
import { DiktatFehler, type DiktatAnschluss, type DiktatFehlerArt, type DiktatLauf } from "./morse-sprechen.js";
import { SPRECHEN } from "./morse-texte.js";
import type { SprachWort } from "./morse-sprechen-leser.js";

/** Aus dem, was `getUserMedia`, der Schlüssel-Abruf oder die Leitung werfen, die Art des Fehlers. */
export function diktatFehlerArt(e: unknown): DiktatFehlerArt {
  const name = e instanceof Error ? e.name : "";
  const text = e instanceof Error ? e.message : String(e ?? "");
  if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") return "recht";
  if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") return "kein-mikro";
  // Schlüssel-Abruf (HTTP …), Verbindung, Dienst (Soniox 402 …): das Netz oder der Dienst.
  if (/Schlüssel|HTTP|WebSocket|Soniox|Failed to fetch|NetworkError|Load failed|network/i.test(text) || name === "TypeError") return "netz";
  return "sonst";
}

export function diktatAusApp(): DiktatAnschluss {
  return {
    sperre: () => {
      if (istAnrufAktiv()) return SPRECHEN.sperre.anruf;
      if (seitenAufnahmeLaeuft()) return SPRECHEN.sperre.sprechtaste;
      if (seitenTonMelder.spielt()) return SPRECHEN.sperre.wiedergabe;
      return null;
    },
    starte: (rueckrufe) => {
      // Die Sitzung weg von `playback`, bevor das Mikrofon kommt.
      sitzungZurueck();
      let woerter: SprachWort[] = [];
      let fertig = false;
      const diktat = new LiveDictation({
        onText: (final, interim, _w, alle, ganz) => {
          woerter = alle.map((w) => ({ text: w.text, konfidenz: w.konfidenz ?? null }));
          // `fest`: nichts Vorläufiges mehr — „Augen zu“ wertet einen klaren Buchstaben dann ohne auf den Abschluss zu warten.
          rueckrufe.beiText(ganz, woerter, final !== "" && interim === "");
        },
        onEnde: () => rueckrufe.beiSatzende(),
        onError: (m) => {
          if (!fertig) rueckrufe.beiFehler(diktatFehlerArt(new Error(m)));
        },
      });
      // Synchron bis zum ersten await (AudioContext in der Geste) — das Versprechen trägt nur den Ausgang.
      const bereit = diktat.start().then(
        () => undefined,
        (e: unknown) => {
          throw new DiktatFehler(diktatFehlerArt(e));
        },
      );
      // Ein Start, der scheiterte, wird vom Knopf über `bereit` gemeldet; hier nur nicht „unbehandelt“ werden.
      bereit.catch(() => undefined);
      const lauf: DiktatLauf = {
        bereit,
        beende: async (warteMs) => {
          fertig = true;
          await bereit.catch(() => undefined);
          // `woerter` tragen auch die vorläufigen Wörter: endet die Frist vor dem Abschluss, ist das Gehörte trotzdem da.
          const text = await diktat.stopUndAbschluss(warteMs);
          return { text, woerter };
        },
        verwirf: () => {
          fertig = true;
          // Erst nach dem Start beenden: ein Stopp mitten im Aufbau ließe Mikrofon oder Kontext offen.
          void bereit.then(
            () => diktat.stop(),
            () => diktat.stop(),
          );
        },
      };
      return lauf;
    },
  };
}
